import { logRumError } from 'lib/DDRum';

import { BOOKS_URL } from '../data/environment';
import { FONT_SIZES } from '../services/font_sizes';

export const getFullPath = (path) => {
  if (path) {
    return `${BOOKS_URL}${path}`;
  }

  return null;
};

export const mapTextSize = (fontSize) => {
  switch (fontSize) {
    case FONT_SIZES.SMALLER:
      return 28;
    case FONT_SIZES.SMALL:
      return 30;
    case FONT_SIZES.REGULAR:
      return 32;
    case FONT_SIZES.LARGE:
      return 34;
    case FONT_SIZES.LARGER:
      return 36;
    default:
      return 32;
  }
};

export const getFontSize = (relativeSize, initialSize) => (relativeSize * initialSize) / FONT_SIZES.REGULAR;

export const formatAudioTime = (value) => {
  if (value.includes(':')) {
    const [h, m, s] = value.split(':');

    return h * 60 * 60 + m * 60 + s * 1;
  }

  return Number(value);
};

export const getAudioFragments = nodes =>
  [...nodes].reduce(
    (acc, node) => {
      const text = node.querySelector('text');
      const audio = node.querySelector('audio');

      const [, textId] = text.getAttribute('src').split('#');
      const clipBegin = formatAudioTime(audio.getAttribute('clipBegin'));
      const clipEnd = formatAudioTime(audio.getAttribute('clipEnd'));
      const audioSrc = audio.getAttribute('src');

      return {
        ...acc,
        src: audioSrc,
        fragments: {
          ...acc.fragments,
          [textId]: {
            elementId: textId,
            clipBegin,
            clipEnd: clipEnd > clipBegin ? clipEnd : clipBegin + clipEnd,
          },
        },
      };
    },
    {
      src: null,
      fragments: {},
    },
  );

export const getChapterIdref = (spines, lockedChapterDocument) => {
  if (!lockedChapterDocument) {
    return lockedChapterDocument;
  }

  const chapter = (spines || []).find(el => el.href === `xhtml/${lockedChapterDocument}.xhtml`) || {};

  return chapter.idref;
};

export const flushPromises = () => new Promise((resolve) => {
  setTimeout(resolve);
});

/**
 * Moves an element in an array from one index to another,
 * returning a new array with the element repositioned.
 * Used with drag and drop - replaces arrayMove from react-sortable-hoc
 *
 * @param {Array} origArray - Original array from which an element is to be moved
 * @param {number} fromIndex - Index of the element to move
 * @param {number} toIndex - Index to which the element should be moved
 * @returns {Array} Array with the element moved to the specified position
 *
 * @example
 * Move element from index 1 to index 2
 * moveElementInArray([1, 2, 3], 1, 2);
 * returns [1, 3, 2]
 *
 * @example
 * // Move last element to first position
 * moveElementInArray([1, 2, 3], -1, 0);
 * returns [3, 1, 2]
*/
export const moveElementInArray = (origArray, fromIndex, toIndex) => {
  // determine the actual start index, accounting for negative indices (which count from the end)
  const startIndex = fromIndex < 0 ? origArray.length + fromIndex : fromIndex;

  // check if the start index is within the bounds of the array
  if (startIndex >= 0 && startIndex < origArray.length) {
    // determine the actual end index, accounting for negative indices
    const endIndex = toIndex < 0 ? origArray.length + toIndex : toIndex;

    const newArray = [...origArray];
    // remove the element from its original position
    const [movedElement] = newArray.splice(fromIndex, 1);
    // insert the element at the new position
    newArray.splice(endIndex, 0, movedElement);
    return newArray;
  }

  // return origArray if indices are out of bounds
  return origArray;
};

const ALLOWED_QUEST_CHAPTERS = {
  Chapter1: ['cover001', 'title001', 'page001'],
  Chapter2: ['page002'],
  Chapter3: ['page003'],
  Chapter4: ['page004'],
};

// temp, to check if the component still has the issue with the unexpected book chapter
export const validateChapterId = ({
  place = '',
  nextChapterId,
  currentChapterId,
  isPractice,
  lockedChapterId,
  shouldSkipLog = false,
}) => {
  if (shouldSkipLog) {
    return;
  }

  const isChapterBookInQuest = !isPractice && !!lockedChapterId;
  if (isChapterBookInQuest && nextChapterId) {
    const isNextChapterAllowed = ALLOWED_QUEST_CHAPTERS[lockedChapterId]?.includes(nextChapterId);

    if (!isNextChapterAllowed) {
      logRumError(`Unexpected book chapter - ${place}`, { nextChapterId, currentChapterId, lockedChapterId });
    }
  }
};
